// Load polyfills (Object.groupBy, ...) before the route tree evaluates, since
// router.tsx is the client entry and routeTree.gen pulls in every route module.
import './polyfills'
import type { ReactNode } from 'react'
import { isServer } from '@tanstack/react-query'
import { createRouter } from '@tanstack/react-router'
import { setupRouterSsrQueryIntegration } from '@tanstack/react-router-ssr-query'
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client'
import { CookiesProvider } from 'react-cookie'
import { routeTree } from './routeTree.gen'
import { makeQueryClient, queryClient as clientQueryClient, persistOptions } from './main'
import { useAppStore } from './lib/app-store'
import { createLocaleI18n, loadTranslation } from './lib/locales'
import { resolveServerLocale } from './lib/server-locale'
import type { Locale } from 'shared/locales'
import { setupAnalytics } from './lib/analytics'
import { DefaultRouteError } from './route-error'
import { logFrontendError } from './lib/log-error'
import { setupVersionDriftReload, reloadForPreloadError } from './lib/app-version'

/**
 * Router factory consumed by TanStack Start (the Start plugin imports `getRouter`
 * from `src/router.tsx`). Replaces the old `createRoot` bootstrap in `main.tsx`.
 *
 * SSR: the app is full-SSR (loaders AND components render on the server). Route loaders run on the
 * server and prefetch through the in-process tRPC link (see `@/main`); deferred (non-awaited) prefetches
 * stream their result so the component server-renders content as the promise resolves. Full SSR is
 * load-bearing for the report routes specifically: the server-rendered HTML is what paints the report on
 * reload (the persisted-cache restore in `@/main` is async/racy), so they must NOT be `ssr: 'data-only'`.
 * The tRPC proxy stays a singleton in `@/main`
 * (imported by ~125 files), but the query client must be per-request on the server —
 * a module singleton would leak one request's dehydrated cache into the next on the
 * long-lived server. So mint a fresh client per `getRouter()` on the server and reuse
 * the persisted singleton on the client; loaders read it via `context.queryClient`.
 */
export async function getRouter() {
  const queryClient = isServer ? makeQueryClient() : clientQueryClient

  // Resolve the request's locale ONCE, here, so SSR and the first client render agree — and load +
  // activate its catalog BEFORE the router is created and React renders/hydrates.
  const locale: Locale = isServer ? (resolveServerLocale() ?? 'es-ES') : useAppStore.getState().store.locale

  // Request-scoped Lingui instance. On the server this isolates each request's active locale from
  // concurrent renders sharing the worker (prevents React #418 hydration mismatches); on the client it
  // is the shared global. The render tree reads it via context (AppShell <I18nProvider> + error
  // boundary). See createLocaleI18n.
  const i18nInstance = createLocaleI18n()
  await loadTranslation(locale, i18nInstance)

  const router = createRouter({
    routeTree,
    // Loaders prefetch into this client via `context.queryClient`; the ssr-query
    // integration below dehydrates the same instance and streams it to the client.
    context: { queryClient, locale, i18n: i18nInstance },
    defaultPreload: 'intent',
    // Since we're using React Query, we don't want loader calls to ever be stale —
    // this ensures the loader runs whenever a route is preloaded or visited.
    defaultPreloadStaleTime: 0,
    // Blank default: every route OWNS its pending UI via its own `pendingComponent` (co-located with the
    // route — see the section/content routes and app-layout-skeleton.tsx). This default just ensures
    // routes WITHOUT a visible skeleton still get wrapped in a Suspense boundary that renders nothing,
    // so their data/chunk suspense is caught in place (the real server-rendered chrome stays put) instead
    // of bubbling up and blanking the whole app. There is no centralized pathname→skeleton dispatcher.
    defaultPendingComponent: () => null,
    // A suspense query throws its error during render; without an error boundary an auth/test error on
    // a streamed query (e.g. anonymous/expired-session /report) crashes the whole tree. This catches it
    // per-route — redirecting auth/test errors to login/test (mirroring toastErrorLink) and otherwise
    // rendering a graceful fallback — so the chrome stays and the app never white-screens.
    // Pass the request-scoped instance so the boundary — which renders ABOVE AppShell's <I18nProvider>
    // and provides its own — localizes against this request's locale, never a concurrent request's
    // (React #418). See createLocaleI18n.
    defaultErrorComponent: (props) => <DefaultRouteError {...props} i18n={i18nInstance} />,
    Wrap: (props: { children: ReactNode }) => {
      return (
        <CookiesProvider defaultSetOptions={{ path: '/' }}>
          <PersistQueryClientProvider client={queryClient} persistOptions={persistOptions}>
            {props.children}
          </PersistQueryClientProvider>
        </CookiesProvider>
      )
    },
  })

  setupRouterSsrQueryIntegration({
    router,
    queryClient,
    wrapQueryClient: false,
  })

  // Google Analytics (client + prod only). Consent-gated: GA loads and tracks only after the visitor
  // opts in to statistics cookies, and reacts to later changes. See lib/analytics.ts + cookie-consent.ts.
  setupAnalytics(router)

  if (!isServer) {
    // When a new version deploys, the hosting layer can delete the previous build's chunks. A client
    // still running the old HTML then fails to import a now-missing chunk. Reload to fetch the new
    // HTML + assets. See https://vite.dev/guide/build#load-error-handling
    //
    // Guarded: a *persistent* import failure (e.g. a transient CDN/CORS blip that outlives one reload)
    // would otherwise loop — reload → same failed import → reload → ∞. reloadForPreloadError reloads at
    // most once per cooldown, then lets the rejected import fall through to the route error boundary.
    window.addEventListener('vite:preloadError', (event) => {
      if (reloadForPreloadError()) event.preventDefault()
    })

    // A new build deployed mid-session keeps the client on stale code (its old chunks still
    // exist on the CDN, so it never errors). Reload on the next navigation to pick up the new
    // version. See lib/app-version.
    setupVersionDriftReload(router)

    // Errors that escape React's error boundaries — async callbacks, event handlers, setTimeout, etc.
    // — never reach `defaultErrorComponent`, so report them here too.
    window.addEventListener('error', (event) => {
      // Cross-origin script errors (GA, third-party embeds) surface with `event.error == null` and no
      // actionable detail — skip them.
      if (event.error == null) return
      logFrontendError(event.error, '[GLOBAL]')
    })

    window.addEventListener('unhandledrejection', (event) => {
      logFrontendError(event.reason, '[UNHANDLED REJECTION]')
    })
  }

  return router
}

declare module '@tanstack/react-router' {
  interface Register {
    router: Awaited<ReturnType<typeof getRouter>>
  }
}
